import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, getApiErrorMessage } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useToast } from '../ui/ToastProvider'
import {
  EMPTY_OVERRIDE,
  Field,
  billingModeLabel,
  contractFromApiRow,
  displayDateTime,
  effectiveStateLabel,
  overrideFromApiRow,
  priceModeLabel,
  seatCountPolicyLabel,
  snapshotStatusLabel,
  statusLabel,
} from './billing/orgBillingUtils'

function parseContractInstant(value) {
  if (value == null || value === '') return null
  const t = new Date(value).getTime()
  return Number.isNaN(t) ? null : t
}

function formatWon(value) {
  const amount = Number(value || 0)
  return `₩${amount.toLocaleString()}`
}

function formatCount(value) {
  const amount = Number(value ?? 0)
  return Number.isFinite(amount) ? amount.toLocaleString() : '-'
}

function quotaUnitLabel(quotaUnit) {
  const unit = String(quotaUnit || '').trim().toUpperCase()
  const labels = {
    COUNT: '건',
    RUN: '회',
    RECIPIENT: '명',
    PAGE: '장',
    ACCESS: '이용',
    DAY: '일',
    MB: 'MB',
    GB: 'GB',
    BYTE: '바이트',
  }
  return labels[unit] || quotaUnit || ''
}

function quotaValueLabel(mode, limitValue, quotaUnit) {
  const value = String(mode || '').toUpperCase()
  if (value === 'UNLIMITED') return '무제한'
  if (value === 'NONE') return '미제공'
  const amount = Number(limitValue || 0)
  const unit = quotaUnitLabel(quotaUnit)
  return `${Number.isFinite(amount) ? amount.toLocaleString() : 0}${unit ? ` ${unit}` : ''}`
}
function appliedMemberCount(org) {
  return org?.approvedMemberCount ?? org?.currentApprovedMembers ?? org?.appliedMemberCount ?? org?.memberCount ?? 0
}

function currentYearMonth() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`
}

function invoiceStatusLabel(value, fallback) {
  const u = String(value || '').toUpperCase()
  if (u === 'DRAFT') return '작성중'
  if (u === 'ISSUED') return '발행'
  if (u === 'PAID') return '입금확인'
  if (u === 'CANCELED') return '취소'
  return fallback || value || '-'
}

function settlementStatusLabel(value, fallback) {
  const u = String(value || '').toUpperCase()
  if (u === 'PENDING') return '정산대기'
  if (u === 'CONFIRMED') return '정산확정'
  if (u === 'CANCELED') return '정산취소'
  return fallback || value || '-'
}
function sortContractsForHistory(a, b) {
  if (Boolean(a.currentlyEffective) !== Boolean(b.currentlyEffective)) {
    return a.currentlyEffective ? -1 : 1
  }
  const ta = parseContractInstant(a.startedAt)
  const tb = parseContractInstant(b.startedAt)
  if (ta != null && tb != null && ta !== tb) return tb - ta
  if (ta != null && tb == null) return -1
  if (ta == null && tb != null) return 1
  return (Number(b.id) || 0) - (Number(a.id) || 0)
}

const DETAIL_TABS = [
  { id: 'contract', label: '계약정보' },
  { id: 'invoices', label: '청구서' },
  { id: 'history', label: '이력' },
]
export default function BillingOrgContractDetailPage() {
  const { contractId: contractIdParam } = useParams()
  const contractId = Number(contractIdParam)
  const { show } = useToast()

  const [activeDetailTab, setActiveDetailTab] = useState('contract')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingContract, setEditingContract] = useState(false)
  const [contractDraft, setContractDraft] = useState(null)
  const [orgContracts, setOrgContracts] = useState([])
  const [pricingOverrides, setPricingOverrides] = useState([])
  const [plans, setPlans] = useState([])
  const [contractTargetOrgs, setContractTargetOrgs] = useState([])
  const [contractTargetsLoading, setContractTargetsLoading] = useState(false)

  const [overrideForm, setOverrideForm] = useState(EMPTY_OVERRIDE)
  const [overridePanelOpen, setOverridePanelOpen] = useState(false)

  const [terminationLoading, setTerminationLoading] = useState(false)
  const [terminationPreview, setTerminationPreview] = useState(null)
  const [terminationSnapshots, setTerminationSnapshots] = useState([])

  const [billingInvoices, setBillingInvoices] = useState([])
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [invoiceGenerating, setInvoiceGenerating] = useState(false)
  const [editingInvoiceId, setEditingInvoiceId] = useState(null)
  const [invoiceDraft, setInvoiceDraft] = useState({ issueDate: '', dueDate: '', memo: '' })
  const [quotaSummary, setQuotaSummary] = useState(null)
  const [quotaDraft, setQuotaDraft] = useState({})
  const [quotaLoading, setQuotaLoading] = useState(false)
  const [quotaSaving, setQuotaSaving] = useState(false)
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [plansRes, contractsRes, overridesRes] = await Promise.all([
        api.get('/billing/admin/config/plans'),
        api.get('/billing/admin/config/org-contracts'),
        api.get('/billing/admin/config/org-pricing-overrides'),
      ])
      setPlans(Array.isArray(plansRes?.data?.data) ? plansRes.data.data : [])
      setOrgContracts(Array.isArray(contractsRes?.data?.data) ? contractsRes.data.data : [])
      setPricingOverrides(Array.isArray(overridesRes?.data?.data) ? overridesRes.data.data : [])
    } catch (e) {
      show('계약 정보를 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    loadData()
  }, [loadData])

  const loadBillingInvoices = useCallback(async () => {
    if (!Number.isFinite(contractId) || contractId <= 0) {
      setBillingInvoices([])
      return []
    }
    setInvoiceLoading(true)
    try {
      const res = await api.get(`/billing/admin/config/org-contracts/${contractId}/invoices`)
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      setBillingInvoices(rows)
      return rows
    } catch (e) {
      setBillingInvoices([])
      show('조직 청구서 정보를 불러오지 못했습니다.', 'error')
      return []
    } finally {
      setInvoiceLoading(false)
    }
  }, [contractId, show])

  useEffect(() => {
    loadBillingInvoices()
  }, [loadBillingInvoices])
  const loadQuotaSummary = useCallback(async () => {
    if (!Number.isFinite(contractId) || contractId <= 0) {
      setQuotaSummary(null)
      setQuotaDraft({})
      return
    }
    setQuotaLoading(true)
    try {
      const res = await api.get(`/billing/admin/config/org-contracts/${contractId}/quota`)
      const data = res?.data?.data || null
      const rows = Array.isArray(data?.features) ? data.features : []
      const nextDraft = {}
      rows.forEach((row) => {
        if (!row?.featureCode) return
        nextDraft[row.featureCode] = {
          quotaMode: row.overrideMode || 'DEFAULT',
          limitValue: row.overrideLimitValue ?? '',
        }
      })
      setQuotaSummary(data)
      setQuotaDraft(nextDraft)
    } catch (e) {
      setQuotaSummary(null)
      setQuotaDraft({})
      show('계약별 쿼터를 불러오지 못했습니다.', 'error')
    } finally {
      setQuotaLoading(false)
    }
  }, [contractId, show])

  useEffect(() => {
    loadQuotaSummary()
  }, [loadQuotaSummary])

  const saveQuotaOverrides = async () => {
    const rows = Array.isArray(quotaSummary?.features) ? quotaSummary.features : []
    if (rows.length === 0) return
    setQuotaSaving(true)
    try {
      const overrides = rows.map((row) => {
        const draft = quotaDraft[row.featureCode] || { quotaMode: row.overrideMode || 'DEFAULT', limitValue: row.overrideLimitValue ?? '' }
        return {
          featureCode: row.featureCode,
          quotaMode: draft.quotaMode || 'DEFAULT',
          limitValue: draft.quotaMode === 'LIMITED' ? Number(draft.limitValue) : null,
        }
      })
      const res = await api.put(`/billing/admin/config/org-contracts/${contractId}/quota-overrides`, { overrides })
      const data = res?.data?.data || null
      const nextRows = Array.isArray(data?.features) ? data.features : []
      const nextDraft = {}
      nextRows.forEach((row) => {
        if (!row?.featureCode) return
        nextDraft[row.featureCode] = {
          quotaMode: row.overrideMode || 'DEFAULT',
          limitValue: row.overrideLimitValue ?? '',
        }
      })
      setQuotaSummary(data)
      setQuotaDraft(nextDraft)
      show('계약별 쿼터를 저장했습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, '계약별 쿼터 저장에 실패했습니다.'), 'error')
    } finally {
      setQuotaSaving(false)
    }
  }
  const contract = useMemo(
    () => orgContracts.find((c) => Number(c.id) === contractId) || null,
    [orgContracts, contractId],
  )

  const sameOrgContracts = useMemo(() => {
    if (!contract?.orgId) return []
    return orgContracts
      .filter((c) => Number(c.orgId) === Number(contract.orgId))
      .sort(sortContractsForHistory)
  }, [orgContracts, contract])

  const effectiveContractOrgIds = useMemo(() => {
    const ids = orgContracts.filter((c) => c.currentlyEffective).map((c) => Number(c.orgId))
    return new Set(ids)
  }, [orgContracts])

  const contractOverrides = useMemo(
    () => pricingOverrides.filter((o) => Number(o.orgContractId) === contractId),
    [pricingOverrides, contractId],
  )

  useEffect(() => {
    if (!contract || editingContract) return
    setContractDraft(contractFromApiRow(contract))
  }, [contract, editingContract])

  useEffect(() => {
    if (!contract?.orgId) {
      setContractTargetOrgs([])
      return undefined
    }
    let cancelled = false
    const loadTargets = async () => {
      setContractTargetsLoading(true)
      try {
        const res = await api.get('/billing/admin/config/org-contract-target-orgs', {
          params: { rootOrgId: Number(contract.orgId), includeDescendants: true },
        })
        const rows = Array.isArray(res?.data?.data) ? res.data.data : []
        if (!cancelled) setContractTargetOrgs(rows)
      } catch (e) {
        if (!cancelled) {
          setContractTargetOrgs([])
          show('하위 조직 적용 범위를 불러오지 못했습니다.', 'error')
        }
      } finally {
        if (!cancelled) setContractTargetsLoading(false)
      }
    }
    loadTargets()
    return () => {
      cancelled = true
    }
  }, [contract?.orgId, show])

  const loadTerminationSnapshots = async () => {
    if (!contractId) return []
    try {
      const res = await api.get(`/billing/admin/config/org-contracts/${contractId}/termination-snapshots`)
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      setTerminationSnapshots(rows)
      return rows
    } catch (e) {
      show('종료 스냅샷을 불러오지 못했습니다.', 'error')
      return []
    }
  }

  const previewTermination = async () => {
    if (!contract) return
    setTerminationLoading(true)
    try {
      const res = await api.post(`/billing/admin/config/org-contracts/${contract.id}/termination-preview`, {
        issueTransitionPromotion: true,
        transitionDiscountPercent: 30,
        transitionPromotionValidDays: 30,
      })
      setTerminationPreview(res?.data?.data ?? null)
      await loadTerminationSnapshots()
      show('종료 미리보기를 불러왔습니다.', 'success')
    } catch (e) {
      show('종료 미리보기에 실패했습니다.', 'error')
    } finally {
      setTerminationLoading(false)
    }
  }

  const executeTermination = async () => {
    if (!contract) return
    const ok = window.confirm('이 조직 계약을 종료 처리하고 대상 사용자를 개인 과금 기준으로 전환하시겠습니까?')
    if (!ok) return
    setTerminationLoading(true)
    try {
      const res = await api.post(`/billing/admin/config/org-contracts/${contract.id}/terminate`, {
        issueTransitionPromotion: true,
        transitionDiscountPercent: 30,
        transitionPromotionValidDays: 30,
      })
      setTerminationPreview(res?.data?.data ?? null)
      await loadData()
      await loadTerminationSnapshots()
      show('조직 계약 종료 처리가 완료되었습니다.', 'success')
    } catch (e) {
      show('조직 계약 종료 처리에 실패했습니다.', 'error')
    } finally {
      setTerminationLoading(false)
    }
  }

  const saveContract = async () => {
    if (!contractDraft?.orgId) {
      show('조직 정보가 없습니다.', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        orgId: Number(contractDraft.orgId),
        billingMode: contractDraft.billingMode,
        appliedPlanCode: contractDraft.appliedPlanCode,
        seatCountPolicy: contractDraft.seatCountPolicy,
        contractedSeats: Number(contractDraft.contractedSeats) || 0,
        unitPrice: Number(contractDraft.unitPrice) || 0,
        status: contractDraft.status,
        contactEmail: contractDraft.contactEmail || null,
        contactPhone: contractDraft.contactPhone || null,
        notes: contractDraft.notes || null,
        startedAt: contractDraft.startedAt || null,
        endsAt: contractDraft.endsAt || null,
        graceUntil: contractDraft.graceUntil || null,
      }
      await api.put(`/billing/admin/config/org-contracts/${contractId}`, payload)
      show('조직 계약이 수정되었습니다.', 'success')
      setEditingContract(false)
      await loadData()
    } catch (e) {
      show(getApiErrorMessage(e, '조직 계약 저장에 실패했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const saveOverride = async () => {
    const ocId = Number(overrideForm.orgContractId || contractId)
    if (!Number.isFinite(ocId) || ocId <= 0) {
      show('조직 계약을 선택해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        orgContractId: ocId,
        planCode: overrideForm.planCode,
        priceMode: overrideForm.priceMode,
        discountRate: Number(overrideForm.discountRate) || 0,
        fixedPrice: Number(overrideForm.fixedPrice) || 0,
        seatFrom: Number(overrideForm.seatFrom) || 0,
        seatTo: Number(overrideForm.seatTo) || 0,
        priorityNo: Number(overrideForm.priorityNo) || 0,
        startedAt: overrideForm.startedAt || null,
        endsAt: overrideForm.endsAt || null,
      }
      if (overrideForm.id) {
        await api.put(`/billing/admin/config/org-pricing-overrides/${overrideForm.id}`, payload)
        show('조직 가격정책이 수정되었습니다.', 'success')
      } else {
        await api.post('/billing/admin/config/org-pricing-overrides', payload)
        show('조직 가격정책이 등록되었습니다.', 'success')
      }
      setOverrideForm({ ...EMPTY_OVERRIDE, orgContractId: String(contractId) })
      setOverridePanelOpen(false)
      loadData()
    } catch (e) {
      show('조직 가격정책 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const startEditOverride = (override) => {
    setOverrideForm(overrideFromApiRow(override))
    setOverridePanelOpen(true)
  }


  const generateCurrentMonthInvoice = async () => {
    if (!contract) return
    setInvoiceGenerating(true)
    try {
      const res = await api.post(`/billing/admin/config/org-contracts/${contract.id}/invoices/generate`, {
        yearMonth: currentYearMonth(),
      })
      const data = res?.data?.data
      await loadBillingInvoices()
      if (data?.locked) {
        show('이미 발행 또는 확정된 청구서가 있어 기존 청구서를 유지했습니다.', 'info')
      } else if (data?.existing) {
        show('현재월 청구서를 다시 계산했습니다.', 'success')
      } else {
        show('현재월 청구서를 생성했습니다.', 'success')
      }
    } catch (e) {
      show(getApiErrorMessage(e, '청구서 생성에 실패했습니다.'), 'error')
    } finally {
      setInvoiceGenerating(false)
    }
  }

  const updateInvoiceStatus = async (invoiceId, status) => {
    if (!invoiceId) return
    if (status === 'CANCELED' && !window.confirm('이 청구서를 취소 처리할까요?')) return
    setInvoiceLoading(true)
    try {
      await api.put(`/billing/admin/config/org-billing-invoices/${invoiceId}/status`, { status })
      await loadBillingInvoices()
      show('청구서 상태를 변경했습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, '청구서 상태 변경에 실패했습니다.'), 'error')
    } finally {
      setInvoiceLoading(false)
    }
  }

  const beginInvoiceEdit = (invoice) => {
    setEditingInvoiceId(invoice.id)
    setInvoiceDraft({
      issueDate: invoice.issueDate || '',
      dueDate: invoice.dueDate || '',
      memo: invoice.memo || '',
    })
  }

  const cancelInvoiceEdit = () => {
    setEditingInvoiceId(null)
    setInvoiceDraft({ issueDate: '', dueDate: '', memo: '' })
  }

  const saveInvoiceEdit = async () => {
    if (!editingInvoiceId) return
    setInvoiceLoading(true)
    try {
      await api.put(`/billing/admin/config/org-billing-invoices/${editingInvoiceId}`, {
        issueDate: invoiceDraft.issueDate || null,
        dueDate: invoiceDraft.dueDate || null,
        memo: invoiceDraft.memo || null,
      })
      cancelInvoiceEdit()
      await loadBillingInvoices()
      show('청구서를 수정했습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, '청구서 수정에 실패했습니다.'), 'error')
    } finally {
      setInvoiceLoading(false)
    }
  }

  const deleteInvoice = async (invoice) => {
    if (!invoice?.id) return
    if (!window.confirm('이 청구서를 삭제할까요? 초안 청구서만 삭제할 수 있습니다.')) return
    setInvoiceLoading(true)
    try {
      await api.delete(`/billing/admin/config/org-billing-invoices/${invoice.id}`)
      if (editingInvoiceId === invoice.id) cancelInvoiceEdit()
      await loadBillingInvoices()
      show('청구서를 삭제했습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, '청구서 삭제에 실패했습니다.'), 'error')
    } finally {
      setInvoiceLoading(false)
    }
  }
  const beginContractEdit = () => {
    if (!contract) return
    setContractDraft(contractFromApiRow(contract))
    setEditingContract(true)
  }

  const cancelContractEdit = () => {
    if (contract) setContractDraft(contractFromApiRow(contract))
    setEditingContract(false)
  }

  if (loading && orgContracts.length === 0) {
    return <section className="py-10 text-center text-slate-500">로딩 중...</section>
  }

  if (!Number.isFinite(contractId) || contractId <= 0) {
    return (
      <section className="space-y-4">
        <p className="text-slate-600">잘못된 계약 번호입니다.</p>
        <Link to=".." className="text-sm text-brand-navy underline">
          목록으로
        </Link>
      </section>
    )
  }

  if (!loading && !contract) {
    return (
      <section className="space-y-4">
        <p className="text-slate-600">해당 조직 계약을 찾을 수 없습니다.</p>
        <Link to=".." className="text-sm text-brand-navy underline">
          목록으로
        </Link>
      </section>
    )
  }

  const d = contractDraft || (contract ? contractFromApiRow(contract) : null)

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to=".." className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
          ← 목록
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">조직 계약 #{contractId}</h1>
          <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor="step-02" />
        </div>
      </div>
      {contract ? (
        <p className="text-slate-600">
          {contract.orgName} <span className="text-slate-400">(조직 ID {contract.orgId})</span>
        </p>
      ) : null}

      <div className="border-b border-slate-200">
        <nav className="flex flex-wrap gap-x-6" aria-label="조직 계약 상세 탭">
          {DETAIL_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`border-b-2 px-1 py-3 text-sm font-semibold ${activeDetailTab === tab.id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              aria-pressed={activeDetailTab === tab.id}
              onClick={() => setActiveDetailTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className={activeDetailTab === 'invoices' ? 'rounded-lg border bg-white p-6' : 'hidden'}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="border-l-4 border-brand-navy pl-3 text-lg font-semibold text-slate-900">자동 청구서/정산</h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={loadBillingInvoices}
              disabled={invoiceLoading}
            >
              새로고침
            </button>
            <button
              type="button"
              className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
              onClick={generateCurrentMonthInvoice}
              disabled={invoiceGenerating || !contract}
            >
              {invoiceGenerating ? '생성 중' : '현재월 청구서 생성'}
            </button>
          </div>
        </div>
        {editingInvoiceId ? (
          <div className="mb-4 rounded border border-slate-200 bg-slate-50/70 p-4">
            <div className="mb-3 text-sm font-semibold text-slate-900">청구서 수정</div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="발행일">
                <input
                  type="date"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={invoiceDraft.issueDate}
                  onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, issueDate: e.target.value }))}
                />
              </Field>
              <Field label="납기일">
                <input
                  type="date"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={invoiceDraft.dueDate}
                  onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, dueDate: e.target.value }))}
                />
              </Field>
              <Field label="메모">
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={invoiceDraft.memo}
                  onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, memo: e.target.value }))}
                />
              </Field>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm text-slate-700 hover:bg-white" onClick={cancelInvoiceEdit} disabled={invoiceLoading}>
                취소
              </button>
              <button type="button" className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50" onClick={saveInvoiceEdit} disabled={invoiceLoading}>
                저장
              </button>
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">청구서</th>
                <th className="px-4 py-3 text-left">기간</th>
                <th className="px-4 py-3 text-left">좌석</th>
                <th className="px-4 py-3 text-left">금액</th>
                <th className="px-4 py-3 text-left">상태</th>
                <th className="px-4 py-3 text-left">정산</th>
                <th className="px-4 py-3 text-left"> </th>
              </tr>
            </thead>
            <tbody>
              {!invoiceLoading && billingInvoices.length === 0 ? (
                <tr className="border-t">
                  <td className="px-4 py-6 text-slate-500" colSpan={7}>
                    아직 생성된 청구서가 없습니다.
                  </td>
                </tr>
              ) : null}
              {billingInvoices.map((item) => (
                <tr key={item.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{item.invoiceNo}</div>
                    <div className="text-xs text-slate-500">발행일 {item.issueDate || '-'}</div>
                    <div className="text-xs text-slate-500">납기 {item.dueDate || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <div>{displayDateTime(item.billingPeriodStart)}</div>
                    <div>{displayDateTime(item.billingPeriodEnd)}</div>
                    <div>{item.prorationDays || 0}/{item.periodDays || 0}일</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{item.seatCount || 0}석</div>
                    <div className="text-xs text-slate-500">{seatCountPolicyLabel(item.seatCountPolicy)}</div>
                    <div className="text-xs text-slate-500">단가 {formatWon(item.unitPrice)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700">
                    <div>공급가 {formatWon(item.supplyAmount)}</div>
                    <div>VAT {formatWon(item.vatAmount)}</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">합계 {formatWon(item.totalAmount)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {invoiceStatusLabel(item.status, item.statusLabel)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-700">
                      {settlementStatusLabel(item.settlementStatus, item.settlementStatusLabel)}
                    </span>
                    {item.settledAt ? <div className="mt-1 text-xs text-slate-500">{displayDateTime(item.settledAt)}</div> : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {item.status === 'DRAFT' ? (
                        <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-slate-50" onClick={() => beginInvoiceEdit(item)} disabled={invoiceLoading}>
                          수정
                        </button>
                      ) : null}
                      {item.status === 'DRAFT' ? (
                        <button type="button" className="rounded border border-rose-200 px-2.5 py-1.5 text-xs text-rose-700 hover:bg-rose-50" onClick={() => deleteInvoice(item)} disabled={invoiceLoading}>
                          삭제
                        </button>
                      ) : null}
                      {item.status === 'DRAFT' ? (
                        <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-slate-50" onClick={() => updateInvoiceStatus(item.id, 'ISSUED')} disabled={invoiceLoading}>
                          발행
                        </button>
                      ) : null}
                      {item.status !== 'PAID' && item.status !== 'CANCELED' ? (
                        <button type="button" className="rounded border border-teal-200 px-2.5 py-1.5 text-xs text-teal-700 hover:bg-teal-50" onClick={() => updateInvoiceStatus(item.id, 'PAID')} disabled={invoiceLoading}>
                          입금확인
                        </button>
                      ) : null}
                      {item.status !== 'PAID' && item.status !== 'CANCELED' ? (
                        <button type="button" className="rounded border border-rose-200 px-2.5 py-1.5 text-xs text-rose-700 hover:bg-rose-50" onClick={() => updateInvoiceStatus(item.id, 'CANCELED')} disabled={invoiceLoading}>
                          취소
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className={activeDetailTab === 'contract' ? 'rounded-lg border bg-white p-4' : 'hidden'}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="border-l-4 border-brand-navy pl-3 text-base font-semibold text-slate-900">계약 정보</h2>
          {!editingContract ? (
            <button type="button" className="rounded border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 hover:bg-slate-50" onClick={beginContractEdit}>
              편집
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={cancelContractEdit} disabled={saving}>
                취소
              </button>
              <button
                type="button"
                className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
                onClick={saveContract}
                disabled={saving}
              >
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          )}
        </div>

        {!editingContract && contract ? (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <dt className="text-xs font-medium text-slate-500">과금 모델</dt>
              <dd className="mt-0.5 text-slate-900">{billingModeLabel(contract.billingMode)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">적용 플랜</dt>
              <dd className="mt-0.5 text-slate-900">{contract.appliedPlanCode || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">상태</dt>
              <dd className="mt-0.5 text-slate-900">
                {statusLabel(contract.status)}
                <span className="ml-2 text-xs text-slate-500">
                  {effectiveStateLabel(contract)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">좌석 정책</dt>
              <dd className="mt-0.5 text-slate-900">{seatCountPolicyLabel(contract.seatCountPolicy)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">계약 좌석 / 승인 인원</dt>
              <dd className="mt-0.5 text-slate-900">
                {contract.contractedSeats || 0} / {contract.currentApprovedMembers ?? 0}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">계약 단가</dt>
              <dd className="mt-0.5 text-slate-900">{formatWon(contract.unitPrice)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">연락</dt>
              <dd className="mt-0.5 text-slate-900">
                {contract.contactEmail || '-'} · {contract.contactPhone || '-'}
              </dd>
            </div>
            <div className="sm:col-span-2 xl:col-span-2">
              <dt className="text-xs font-medium text-slate-500">계약 기간</dt>
              <dd className="mt-0.5 text-slate-900">
                시작 {displayDateTime(contract.startedAt)} · 종료 {displayDateTime(contract.endsAt)} · 유예 {displayDateTime(contract.graceUntil)}
              </dd>
            </div>
            <div className="sm:col-span-2 xl:col-span-2">
              <dt className="text-xs font-medium text-slate-500">메모</dt>
              <dd className="mt-0.5 rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-slate-800">{contract.notes || '—'}</dd>
            </div>
          </dl>
        ) : d ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="조직">
              <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {contract?.orgName} (#{contract?.orgId})
              </div>
            </Field>
            <Field label="과금 모델">
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.billingMode}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, billingMode: e.target.value }))}
              >
                <option value="ORG_PAYS">조직 결제 (단체 계약)</option>
              </select>
            </Field>
            <Field label="적용 플랜">
              <select
                  className="w-full rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700"
                  value="ENTERPRISE"
                  disabled
                >
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
            </Field>
            <Field label="상태">
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.status}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="ACTIVE">활성</option>
                <option value="TERMINATING">종료 예정(유예)</option>
                <option value="INACTIVE">비활성</option>
              </select>
            </Field>
            <Field label="좌석 정책">
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.seatCountPolicy}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, seatCountPolicy: e.target.value }))}
              >
                <option value="APPROVED_MEMBER_COUNT">승인된 멤버 수 기준</option>
                <option value="CONTRACTED_SEATS">계약 좌석 수 기준</option>
              </select>
            </Field>
            <Field label="계약 좌석 수">
              <input
                type="number"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.contractedSeats}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, contractedSeats: e.target.value }))}
              />
            </Field>
            <Field label="월 단가">
              <input
                type="number"
                min="0"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.unitPrice}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, unitPrice: e.target.value }))}
              />
            </Field>
            <Field label="연락 이메일">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.contactEmail}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, contactEmail: e.target.value }))}
              />
            </Field>
            <Field label="연락 전화번호">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.contactPhone}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, contactPhone: e.target.value }))}
              />
            </Field>
            <Field label="계약 시작일">
              <input
                type="datetime-local"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.startedAt}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, startedAt: e.target.value }))}
              />
            </Field>
            <Field label="계약 종료일">
              <input
                type="datetime-local"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.endsAt}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, endsAt: e.target.value }))}
              />
            </Field>
            <Field label="유예 종료일">
              <input
                type="datetime-local"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.graceUntil}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, graceUntil: e.target.value }))}
              />
            </Field>
            <Field label="메모">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.notes}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </Field>
          </div>
        ) : null}
      </div>

      <div className={activeDetailTab === 'contract' ? 'rounded-lg border bg-white p-4' : 'hidden'}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="border-l-4 border-brand-navy pl-3 text-base font-semibold text-slate-900">계약별 쿼터</h2>
            <p className="mt-1 text-xs text-slate-500">
              {quotaSummary?.period?.periodStartAtDisplay || quotaSummary?.period?.periodStartAt || '-'} ~ {quotaSummary?.period?.periodEndAtDisplay || quotaSummary?.period?.periodEndAt || '-'}
            </p>
          </div>
          <button
            type="button"
            className="rounded-md bg-brand-navy px-3 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
            onClick={saveQuotaOverrides}
            disabled={quotaLoading || quotaSaving || !quotaSummary}
          >
            {quotaSaving ? '저장 중...' : '쿼터 저장'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">기능</th>
                <th className="px-3 py-2 text-left">플랜 기본값</th>
                <th className="px-3 py-2 text-left">계약 설정</th>
                <th className="px-3 py-2 text-right">사용</th>
                <th className="px-3 py-2 text-right">잔여</th>
              </tr>
            </thead>
            <tbody>
              {quotaLoading ? (
                <tr className="border-t"><td className="px-3 py-5 text-slate-500" colSpan={5}>불러오는 중...</td></tr>
              ) : null}
              {!quotaLoading && (!Array.isArray(quotaSummary?.features) || quotaSummary.features.length === 0) ? (
                <tr className="border-t"><td className="px-3 py-5 text-slate-500" colSpan={5}>표시할 쿼터가 없습니다.</td></tr>
              ) : null}
              {(Array.isArray(quotaSummary?.features) ? quotaSummary.features : []).map((row) => {
                const draft = quotaDraft[row.featureCode] || { quotaMode: row.overrideMode || 'DEFAULT', limitValue: row.overrideLimitValue ?? '' }
                const isLimited = draft.quotaMode === 'LIMITED'
                return (
                  <tr key={row.featureCode} className="border-t border-slate-100 align-middle">
                    <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                    <td className="px-3 py-2 text-slate-700">{quotaValueLabel(row.baseQuotaMode, row.baseLimitValue, row.quotaUnit)}</td>
                    <td className="px-3 py-2">
                      <div className="flex min-w-[220px] items-center gap-2">
                        <select
                          className="min-w-0 rounded border px-2 py-1.5 text-sm"
                          value={draft.quotaMode}
                          onChange={(e) => setQuotaDraft((prev) => ({ ...prev, [row.featureCode]: { ...draft, quotaMode: e.target.value } }))}
                          disabled={quotaSaving}
                        >
                          <option value="DEFAULT">기본값 사용</option>
                          <option value="LIMITED">제한</option>
                          <option value="UNLIMITED">무제한</option>
                          <option value="NONE">미제공</option>
                        </select>
                        {isLimited ? (
                          <input
                            type="number"
                            min="1"
                            className="w-24 rounded border px-2 py-1.5 text-right text-sm"
                            value={draft.limitValue}
                            onChange={(e) => setQuotaDraft((prev) => ({ ...prev, [row.featureCode]: { ...draft, limitValue: e.target.value } }))}
                            disabled={quotaSaving}
                            aria-label={`${row.name} 제한 수량`}
                          />
                        ) : null}
                        {isLimited ? <span className="whitespace-nowrap text-xs text-slate-500">{quotaUnitLabel(row.quotaUnit)}</span> : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">
                      {formatCount(row.used)}{Number(row.reserved || 0) > 0 ? ` + 예약 ${formatCount(row.reserved)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">{quotaValueLabel(row.quotaMode, row.remaining, row.quotaUnit)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className={activeDetailTab === 'history' ? 'rounded-lg border bg-white p-6' : 'hidden'}>
        <h2 className="mb-4 border-l-4 border-brand-navy pl-3 text-lg font-semibold text-slate-900">계약 이력</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">계약</th>
                <th className="px-4 py-3 text-left">과금</th>
                <th className="px-4 py-3 text-left">플랜</th>
                <th className="px-4 py-3 text-left">상태</th>
                <th className="px-4 py-3 text-left">기간</th>
                <th className="px-4 py-3 text-left"> </th>
              </tr>
            </thead>
            <tbody>
              {sameOrgContracts.map((item) => (
                <tr key={item.id} className={`border-t border-slate-100 ${Number(item.id) === contractId ? 'bg-teal-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    #{item.id}
                    {Number(item.id) === contractId ? <span className="ml-2 text-xs font-medium text-teal-700">현재 화면</span> : null}
                  </td>
                  <td className="px-4 py-3">{billingModeLabel(item.billingMode)}</td>
                  <td className="px-4 py-3">{item.appliedPlanCode || '-'}</td>
                  <td className="px-4 py-3">
                    <div>{statusLabel(item.status)}</div>
                    <div className="text-xs text-slate-500">{effectiveStateLabel(item)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <div>시작 {displayDateTime(item.startedAt)}</div>
                    <div>종료 {displayDateTime(item.endsAt)}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`../contracts/${item.id}`} className="inline-flex rounded border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                      상세
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={activeDetailTab === 'contract' ? 'rounded-lg border bg-white p-4' : 'hidden'}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="border-l-4 border-brand-navy pl-3 text-base font-semibold text-slate-900">하위 조직 적용 범위</h2>
          {contractTargetsLoading ? <span className="text-xs text-slate-500">불러오는 중…</span> : null}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">조직ID</th>
                <th className="px-3 py-2 text-left font-semibold">조직명</th>
                <th className="px-3 py-2 text-left font-semibold">구분</th>
                <th className="px-3 py-2 text-right font-semibold">적용인원수</th>
                <th className="px-3 py-2 text-left font-semibold">직접계약</th>
              </tr>
            </thead>
            <tbody>
              {!contractTargetsLoading && contractTargetOrgs.length === 0 ? (
                <tr className="border-t">
                  <td className="px-3 py-5 text-slate-500" colSpan={5}>
                    표시할 하위 조직이 없습니다.
                  </td>
                </tr>
              ) : null}
              {contractTargetOrgs.map((org) => {
                const isCurrentContractOrg = Number(org.id) === Number(contract?.orgId)
                const hasDirectContract = isCurrentContractOrg || effectiveContractOrgIds.has(Number(org.id))
                return (
                  <tr key={org.id} className="border-t border-slate-100">
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600">{org.id}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{org.name}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">{org.isRoot ? '기준 조직' : '하위 조직'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-slate-900">{formatCount(appliedMemberCount(org))}명</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {hasDirectContract ? (
                        <span className={`text-xs font-medium ${isCurrentContractOrg ? 'text-brand-navy' : 'text-amber-700'}`}>
                          {isCurrentContractOrg ? '현재 계약' : '유효 계약 있음'}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">상위 계약 적용</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className={activeDetailTab === 'contract' ? 'rounded-lg border bg-white p-4' : 'hidden'}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="border-l-4 border-brand-navy pl-3 text-lg font-semibold text-slate-900">가격정책</h2>
          <button
            type="button"
            className="rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
            onClick={() => {
              setOverrideForm({ ...EMPTY_OVERRIDE, orgContractId: String(contractId) })
              setOverridePanelOpen(true)
            }}
          >
            가격정책 추가
          </button>
        </div>

        {overridePanelOpen ? (
          <div className="mb-6 rounded border border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">{overrideForm.id ? '가격정책 수정' : '가격정책 등록'}</h3>
              <button
                type="button"
                className="text-sm text-slate-600 hover:text-slate-900"
                onClick={() => {
                  setOverridePanelOpen(false)
                  setOverrideForm({ ...EMPTY_OVERRIDE, orgContractId: String(contractId) })
                }}
              >
                닫기
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="조직 계약">
                <div className="rounded border bg-slate-100 px-3 py-2 text-sm text-slate-600">#{contractId}</div>
              </Field>
              <Field label="플랜">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.planCode}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, planCode: e.target.value }))}
                >
                  {plans.map((plan) => (
                    <option key={plan.planCode} value={plan.planCode}>
                      {plan.planCode}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="가격 정책">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.priceMode}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, priceMode: e.target.value }))}
                >
                  <option value="PERCENT_DISCOUNT">정률 할인(%)</option>
                  <option value="FIXED_PRICE">고정 단가</option>
                </select>
              </Field>
              <Field label="할인율(%)">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.discountRate}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, discountRate: e.target.value }))}
                />
              </Field>
              <Field label="고정 가격">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.fixedPrice}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, fixedPrice: e.target.value }))}
                />
              </Field>
              <Field label="우선순위">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.priorityNo}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, priorityNo: e.target.value }))}
                />
              </Field>
              <Field label="좌석 구간 시작">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.seatFrom}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, seatFrom: e.target.value }))}
                />
              </Field>
              <Field label="좌석 구간 끝">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.seatTo}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, seatTo: e.target.value }))}
                />
              </Field>
              <Field label="적용 시작일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.startedAt}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, startedAt: e.target.value }))}
                />
              </Field>
              <Field label="적용 종료일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.endsAt}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, endsAt: e.target.value }))}
                />
              </Field>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
                onClick={saveOverride}
                disabled={saving}
              >
                {saving ? '저장 중…' : overrideForm.id ? '가격정책 수정' : '가격정책 등록'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">플랜</th>
                <th className="px-4 py-3 text-left">정책</th>
                <th className="px-4 py-3 text-left">가격</th>
                <th className="px-4 py-3 text-left">좌석 구간</th>
                <th className="px-4 py-3 text-left">적용 기간</th>
                <th className="px-4 py-3 text-left"> </th>
              </tr>
            </thead>
            <tbody>
              {contractOverrides.length === 0 && (
                <tr className="border-t">
                  <td className="px-4 py-6 text-slate-500" colSpan={6}>
                    이 계약에 등록된 가격정책이 없습니다.
                  </td>
                </tr>
              )}
              {contractOverrides.map((item) => (
                <tr key={item.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{item.planCode}</td>
                  <td className="px-4 py-3">{priceModeLabel(item.priceMode)}</td>
                  <td className="px-4 py-3">
                    {item.priceMode === 'FIXED_PRICE' ? `₩${Number(item.fixedPrice || 0).toLocaleString()}` : `${Number(item.discountRate || 0)}%`}
                  </td>
                  <td className="px-4 py-3">
                    {item.seatFrom} ~ {item.seatTo}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>시작 {displayDateTime(item.startedAt)}</div>
                    <div>종료 {displayDateTime(item.endsAt)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <button type="button" className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50" onClick={() => startEditOverride(item)}>
                      수정
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={activeDetailTab === 'contract' ? 'rounded-lg border border-amber-200 bg-amber-50/40 p-4' : 'hidden'}>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">계약 종료</h2>
        <p className="mb-4 text-sm text-slate-600">종료 미리보기로 영향 사용자를 확인한 뒤 종료 처리할 수 있습니다.</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={previewTermination}
            disabled={terminationLoading || !contract}
          >
            종료 미리보기
          </button>
          {contract?.status !== 'INACTIVE' ? (
            <button
              type="button"
              className="rounded border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              onClick={executeTermination}
              disabled={terminationLoading}
            >
              종료 처리
            </button>
          ) : null}
          <button
            type="button"
            className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={loadTerminationSnapshots}
            disabled={terminationLoading}
          >
            스냅샷 보기
          </button>
        </div>

        {terminationPreview?.contract?.id === contractId ? (
          <div className="mt-4 space-y-2 text-sm">
            <div className="font-semibold text-slate-900">영향 사용자 미리보기</div>
            <div>대상 인원: {terminationPreview?.affectedUserCount || 0}명</div>
            <div>
              전환 할인: {terminationPreview?.transitionDiscountPercent || 0}% / 유효 {terminationPreview?.transitionPromotionValidDays || 0}일
            </div>
            <div className="overflow-x-auto rounded border border-amber-100 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">이름</th>
                    <th className="px-3 py-2 text-left">이메일</th>
                    <th className="px-3 py-2 text-left">이전</th>
                    <th className="px-3 py-2 text-left">전환 후</th>
                  </tr>
                </thead>
                <tbody>
                  {(terminationPreview?.affectedUsers || terminationPreview?.processedUsers || []).map((item) => (
                    <tr key={item.userId} className="border-t border-amber-50">
                      <td className="px-3 py-2">{item.userName}</td>
                      <td className="px-3 py-2">{item.userEmail}</td>
                      <td className="px-3 py-2">
                        {item.beforeBillingMode} / {item.beforePlanCode}
                      </td>
                      <td className="px-3 py-2">
                        {item.targetBillingMode} / {item.targetPlanCode}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {terminationSnapshots.length > 0 ? (
          <div className="mt-4">
            <div className="mb-2 text-sm font-medium text-slate-800">저장된 종료 스냅샷</div>
            <div className="overflow-x-auto rounded border border-amber-100 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">이름</th>
                    <th className="px-3 py-2 text-left">이메일</th>
                    <th className="px-3 py-2 text-left">전환 플랜</th>
                    <th className="px-3 py-2 text-left">쿠폰</th>
                    <th className="px-3 py-2 text-left">상태</th>
                    <th className="px-3 py-2 text-left">처리 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {terminationSnapshots.map((item) => (
                    <tr key={item.id} className="border-t border-amber-50">
                      <td className="px-3 py-2">{item.userName}</td>
                      <td className="px-3 py-2">{item.userEmail}</td>
                      <td className="px-3 py-2">
                        {item.targetBillingMode} / {item.targetPlanCode}
                      </td>
                      <td className="px-3 py-2">{item.transitionPromotionCode || '-'}</td>
                      <td className="px-3 py-2">{snapshotStatusLabel(item.processingStatus)}</td>
                      <td className="px-3 py-2">{displayDateTime(item.processedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
